// frontend/src/pages/reports/DeadStockReport.jsx
// Report: Dead Stock / Inventory Aging
//
// Identifies inventory with no recent sales, grouped by aging buckets.
// Merchant question: "Which products haven't sold and what are they costing me?"

import React, { useState, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AgGridReact } from 'ag-grid-react';
import { Button, Tooltip } from '@mui/material';
import PageBanner from '../../components/common/PageBanner';
import Tile from '../../components/layout/Tile';
import ReportKpiStrip from '../../components/reports/ReportKpiStrip';
import PartialPriceChip from '../../components/reports/PartialPriceChip';
import { ItemFilterBar, useItemFilter } from '../../features/item-filter';
import { useSsrmGrid, buildExportQuery } from '../../hooks/useSsrmGrid';
import { useIsShopify, itemIdentityColumns } from '../../components/grid/productIdentity';
import { useAgGridTheme } from '../../utils/agGridTheme';
import { useColumnTypes } from '../../hooks/useColumnTypes';
import { useFormatters } from '../../hooks/useFormatters';
import { useProductGrouping, VARIANT_ROW_SELECTION } from '../../components/grid/productGrouping';
import { useReportFilters } from '../../hooks/useReportFilters';
import {
  useCreatePromotionFromDeadStock,
  useMarkItemsNPI,
} from '../../hooks/useReportActionMutations';
import { translateRequestError } from '../../queries/api';
import { showToast } from '../../components/common/appToastBus';
import { usePromotionResult } from './usePromotionResult';
import { AUTO_SIZE_TO_FILL, defaultColDef, defaultGridProps, copyOnlyContextMenuItems, restoreInitialSort } from '../../utils/gridDefaults';
import { scopeHeaderCheckboxToPage } from '../../utils/gridSelection';
import GridExportButton from '../../components/common/GridExportButton';
import { buildPinnedTotalsRow, wrapColumnsForSummaryRows } from '../../utils/pinnedTotals';
import '../../styles/pages.css';
import './DeadStockReport.css';
import { GridEmptyState } from '../../components/grid/NoRowsOverlay';
import { GridPage, GridViewport } from '../../components/grid/GridPage';



const AGING_COLORS = {
  watch: 'var(--color-warning)',
  at_risk: 'var(--color-warning)',
  dead: 'var(--color-error)',
};

const AGING_BG = {
  watch: 'var(--color-warning-bg)',
  at_risk: 'var(--color-warning-bg)',
  dead: 'var(--color-error-bg)',
};

function AgingChip({ value, t }) {
  if (!value) return null;
  const label = t(`reports.deadStock.aging.${value}`);
  return (
    <span
      className="dead-stock-aging-chip"
      style={{ color: AGING_COLORS[value], backgroundColor: AGING_BG[value] }}
    >
      {label}
    </span>
  );
}

function DeadStockReport() {
  const { t } = useTranslation();
  const isShopify = useIsShopify();
  const gridTheme = useAgGridTheme();
  const columnTypes = useColumnTypes();
  const { formatNumber, formatCompact } = useFormatters();
  const defaultHidden = useMemo(() => ['shopify_product_title'], []);
  // Group variants by product (productGrouping): the switch, the product
  // tree, the row id and the saved column state, one preference for every
  // report. A product row carries the report's totals restricted to that
  // product and the report's own measure over it; only those columns say
  // `sortable: true`, since the server orders the products by them.
  const grouping = useProductGrouping({
    gridId: 'report_dead_stock',
    preferenceKey: 'reports_grouped',
    defaultHidden,
    leafIdFields: ['item_id', 'location_id'],
  });
  const { isGrouped, columns: groupedColumns, variantProductColumn, totalsLabelField } = grouping;
  const gridDefaultColDef = useMemo(
    () => ({ ...defaultColDef, ...grouping.defaultColDef }),
    [grouping.defaultColDef],
  );
  const gridRef = useRef(null);

  const itemFilter = useItemFilter();
  const { location, setLocation, productCategory, setProductCategory } = useReportFilters('dead_stock');
  const params = useMemo(() => ({ location_code: location, product_category_id: productCategory?.id || null, ...itemFilter.params }), [location, productCategory, itemFilter.params]);

  const [chrome, setChrome] = useState({
    summary: null, currency: null, totals: null,
  });
  const onData = useCallback((data) => {
    setChrome({
      summary: data.summary ?? null,
      currency: data.currency ?? null,
      totals: data.totals ?? null,
    });
  }, []);
  const { onGridReady, onGridPreDestroyed, loading, error, pagination } = useSsrmGrid({
    endpoint: '/api/reports/dead-stock/data/',
    params,
    onData,
    gridRef,
  });
  const { summary, currency, totals } = chrome;

  const showPromotionResult = usePromotionResult('deadStock');

  // Server-side export of the FULL filtered dataset. Filter values come
  // from the params object so the exported file matches what the merchant
  // sees in the grid.
  const exportProps = {
    endpoint: '/api/reports/dead-stock/export/',
    filename: 'dead_stock',
    getParams: useCallback(
      (scope) => (scope === 'all' ? '' : buildExportQuery(params)),
      [params],
    ),
  };

  const data = useMemo(() => ({ summary, currency }), [summary, currency]);

  // KPI filter — applies a sort on the grid; SSRM relays the sortModel to
  // the backend ?ordering= param via the datasource.
  const [kpiFilter, setKpiFilter] = useState(null);
  const handleKpiClick = useCallback((key) => {
    const newKey = kpiFilter === key ? null : key;
    setKpiFilter(newKey);

    const api = gridRef.current;
    if (!api) return;
    if (newKey === 'dead_value') {
      api.applyColumnState({ state: [{ colId: 'stock_value', sort: 'desc' }], defaultState: { sort: null } });
    } else if (newKey === 'carrying_cost') {
      api.applyColumnState({ state: [{ colId: 'monthly_carrying_cost', sort: 'desc' }], defaultState: { sort: null } });
    } else {
      restoreInitialSort(api);
    }
  }, [kpiFilter]);

  // Row selection for actions. SSRM `getSelectedRows()` returns rows from
  // currently-loaded pages — selecting across page boundaries works as long
  // as the merchant doesn't purge the cache between selecting and acting.
  // The HEADER checkbox is the exception: it produces a select-all rule that
  // getSelectedRows() reads as empty, so it is confined to the current page
  // below. Both actions take a materialised list (create-promotion an item
  // list, mark-NPI a set of item ids), so the selection cannot outrun what
  // the grid has loaded without those endpoints accepting a selection rule.
  const [selectedCount, setSelectedCount] = useState(0);
  const createPromotionMutation = useCreatePromotionFromDeadStock();
  const markNpiMutation = useMarkItemsNPI();
  const creating = createPromotionMutation.isPending;
  const markingNpi = markNpiMutation.isPending;

  const handleSelectionChanged = useCallback((e) => {
    scopeHeaderCheckboxToPage(e.api);
    setSelectedCount(e.api.getSelectedRows().length);
  }, []);

  const handleCreatePromotion = useCallback(async () => {
    const api = gridRef.current;
    if (!api) return;

    const selectedRows = api.getSelectedRows();
    if (!selectedRows.length) return;

    const items = selectedRows.map(r => ({
      item_id: r.item_id,
      location_id: r.location_id,
      item_code: r.item_code,
    }));

    try {
      const result = await createPromotionMutation.mutateAsync({ items });
      api.deselectAll();
      showPromotionResult(result);
    } catch (err) {
      console.error('createPromotionFromDeadStock:', err.message);
      showToast(translateRequestError(err, t, 'reports.deadStock.actions.createPromoFailed'), 'error');
    }
  }, [createPromotionMutation, t, showPromotionResult]);

  const handleMarkNewProduct = useCallback(async () => {
    const api = gridRef.current;
    if (!api) return;

    const selectedRows = api.getSelectedRows();
    if (!selectedRows.length) return;

    const itemIds = [...new Set(selectedRows.map(r => r.item_id))];

    try {
      const result = await markNpiMutation.mutateAsync({ itemIds });
      api.deselectAll();
      showToast(
        [
          result.marked > 0 && t('reports.deadStock.actions.markNewProductSuccess', { count: result.marked }),
          result.already_npi > 0 && t('reports.deadStock.actions.alreadyNewProduct', { count: result.already_npi }),
        ].filter(Boolean).join(' '),
        result.marked > 0 ? 'success' : 'info',
      );
    } catch (err) {
      console.error('markItemsNPI:', err.message);
      showToast(translateRequestError(err, t, 'reports.deadStock.actions.markNewProductFailed'), 'error');
    }
  }, [markNpiMutation, t]);

  const kpiItems = useMemo(() => {
    if (!data?.summary) return [];
    const s = data.summary;
    const currency = data.currency;
    // Null when not one aging row could be priced — unknown, not zero. The
    // rows are still listed (their age is knowable without a cost price); it
    // is only the money that cannot be stated, and the chip beside the grid
    // names the remedy.
    const formattedDeadValue = s.total_dead_value == null
      ? '—'
      : formatCompact(parseFloat(s.total_dead_value), currency);
    const formattedCarrying = s.annual_carrying_cost == null
      ? '—'
      : formatCompact(parseFloat(s.annual_carrying_cost), currency);
    return [
      { label: t('reports.deadStock.kpi.deadValue'), value: formattedDeadValue, key: 'dead_value', tooltip: t('reports.deadStock.kpi.deadValueTooltip') },
      { label: t('reports.deadStock.kpi.pctInventory'), value: s.pct_of_inventory ?? '—', suffix: s.pct_of_inventory == null ? '' : '%', key: 'pct', tooltip: t('reports.deadStock.kpi.pctInventoryTooltip') },
      { label: t('reports.deadStock.kpi.totalItems'), value: s.total_items, key: 'all', tooltip: t('reports.deadStock.kpi.totalItemsTooltip') },
      { label: t('reports.deadStock.kpi.carryingCost'), value: formattedCarrying, suffix: '/yr', key: 'carrying_cost', tooltip: t('reports.deadStock.kpi.carryingCostTooltip') },
      ...(s.npi_count > 0 ? [{ label: t('reports.deadStock.kpi.npiExcluded'), value: s.npi_count, key: 'npi', tooltip: t('reports.deadStock.kpi.npiExcludedTooltip') }] : []),
    ];
  }, [data, t, formatCompact]);

  // Pinned-bottom totals row reads `totals` computed server-side over the
  // FULL filtered dataset (not just the current page). Server payload is
  // `{ field: number }` keyed by the column field names below.
  const pinnedBottomRowData = useMemo(
    () => buildPinnedTotalsRow(totals, totalsLabelField('item_code'), t('grid.totals.label')),
    [totals, t, totalsLabelField],
  );

  const deadStockCurrency = data?.currency;
  const currencySuffix = deadStockCurrency ? ` (${deadStockCurrency})` : '';
  const decimalFormatter = useCallback(
    (p) => p.value != null ? formatNumber(p.value, 2) : '—',
    [formatNumber]
  );

  const columnDefs = useMemo(() => wrapColumnsForSummaryRows([
    // Identity
    ...groupedColumns([variantProductColumn()]),
    ...(isGrouped ? [] : itemIdentityColumns(isShopify, {
      productHeader: t('reports.columns.product'),
      codeHeader: t('reports.columns.itemCode'),
      nameHeader: t('reports.columns.itemName'),
    })),
    { field: 'category_label', headerName: t('reports.columns.productCategory'), minWidth: 100 },
    { field: 'location_name', headerName: t('reports.columns.location'), minWidth: 100 },
    { field: 'supplier_name', headerName: t('reports.deadStock.columns.supplier') },
    { field: 'abc_class', headerName: t('reports.columns.abcClass'), type: 'centeredColumn' },
    // Aging
    {
      field: 'aging_bucket',
      headerName: t('reports.deadStock.columns.agingBucket'),
      headerTooltip: t('reports.deadStock.tooltips.agingBucket'),
      cellRenderer: (p) => <AgingChip value={p.value} t={t} />,
      minWidth: 110,
    },
    {
      field: 'days_since_last_sale',
      headerName: t('reports.deadStock.columns.daysSinceLastSale'),
      type: 'numberColumn',
      valueFormatter: (p) => p.value != null ? formatNumber(p.value, 0) : t('reports.deadStock.neverSold'),
    },
    { field: 'last_sale_date', headerName: t('reports.deadStock.columns.lastSaleDate'), type: 'dateColumn' },
    // Stock position
    { field: 'available', sortable: true, headerName: t('reports.deadStock.columns.available'), type: 'numberColumn' },
    { field: 'on_hand', sortable: true, headerName: t('reports.deadStock.columns.onHand'), type: 'numberColumn' },
    { field: 'committed', sortable: true, headerName: t('reports.deadStock.columns.committed'), type: 'numberColumn' },
    // Value
    { field: 'unit_cost', headerName: t('reports.deadStock.columns.costPrice') + currencySuffix, type: 'decimalColumn', valueFormatter: decimalFormatter },
    { field: 'stock_value', sortable: true, initialSort: 'desc', headerName: t('reports.deadStock.columns.stockValue') + currencySuffix, type: 'decimalColumn', valueFormatter: decimalFormatter },
    { field: 'monthly_carrying_cost', sortable: true, headerName: t('reports.deadStock.columns.carryingCost') + currencySuffix, type: 'decimalColumn', valueFormatter: decimalFormatter, headerTooltip: t('reports.deadStock.tooltips.carryingCost') },
    // Demand confirmation
    { field: 'sales_12m', sortable: true, headerName: t('reports.deadStock.columns.sales12m'), type: 'numberColumn' },
    { field: 'forecast_30d', sortable: true, headerName: t('reports.deadStock.columns.forecast30d'), type: 'numberColumn' },
  ]), [isShopify, isGrouped, groupedColumns, variantProductColumn, t, formatNumber, currencySuffix, decimalFormatter]);

  return (
    <div className="page-container">
      <ReportKpiStrip
        title={t('reports.deadStock.kpi.title')}
        items={kpiItems}
        loading={loading}
        activeKey={kpiFilter}
        onCardClick={handleKpiClick}
        storageKey="dead_stock"
      />

      <PageBanner severity="error" messages={[error && translateRequestError(error, t, 'common.fetchError')]} />

      <div className="tiles-row">
        <Tile
          width="full"
          title={t('nav.reportDeadStock')}
          headerActions={<GridExportButton {...exportProps} />}
        >
          <GridPage>
            <div className="report-caveats">
              <PartialPriceChip
                gap="cost"
                count={summary?.uncosted_items}
                disclose={summary?.partial_cost}
              />
            </div>
            <ItemFilterBar
              filter={itemFilter}
              productCategoryFilter={{ selected: productCategory, set: setProductCategory, show: true }}
              locationFilter={{ selected: location, set: setLocation }}
              actions={
                <>
                  {grouping.toggle}
                  {selectedCount > 0 && (
                    <>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleCreatePromotion}
                        loading={creating}
                      >
                        {t('reports.deadStock.actions.createPromotion', { count: selectedCount })}
                      </Button>
                      <Tooltip title={t('reports.deadStock.actions.markNewProductTooltip')}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleMarkNewProduct}
                          loading={markingNpi}
                        >
                          {t('reports.deadStock.actions.markNewProduct', { count: selectedCount })}
                        </Button>
                      </Tooltip>
                    </>
                  )}
                </>
              }
            />
            <GridViewport>
              {gridTheme && (
                <GridEmptyState
                  filtersActive={location !== 'all' || !!productCategory || !!itemFilter.filter}
                >
                  <AgGridReact
                    theme={gridTheme} columnDefs={columnDefs} columnTypes={columnTypes}
                    loading={loading}
                    {...defaultGridProps} defaultColDef={gridDefaultColDef}
                    autoSizeStrategy={AUTO_SIZE_TO_FILL}
                    rowSelection={VARIANT_ROW_SELECTION}
                    onSelectionChanged={handleSelectionChanged}
                    {...grouping.gridProps}
                    rowModelType="serverSide"
                    {...pagination}
                    onGridReady={onGridReady}
                    onGridPreDestroyed={onGridPreDestroyed}
                    pinnedBottomRowData={pinnedBottomRowData}
                    getContextMenuItems={copyOnlyContextMenuItems}
                  />
                </GridEmptyState>
              )}
            </GridViewport>
          </GridPage>
        </Tile>
      </div>
    </div>
  );
}

export default DeadStockReport;
